// Per-param processor for the signup-URL `groupId` param. Called from
// app/_layout.web.tsx exactly once per onboarding handoff; pulled out here
// so the branching is testable in isolation. Other signup-URL params (e.g.
// a future `includeFullNameStep`) will get their own processors in
// whichever part of the codebase they affect — this one is scoped narrowly
// so each param's consumption lives next to the logic it influences.
//
// Behavior:
// 1. Read the persisted groupId. If there is none, return "no-action".
// 2. Synchronously clear ONLY the groupId field — leaves other persisted
//    signup params alone for their own processors. Doing the clear up
//    front prevents a re-fire of the caller's effect from reading the
//    same groupId and kicking off a second createGroupJoinRequest. The
//    helper's own already-pending guard is the belt to this brace, but
//    the synchronous clear catches the race even when the second call
//    hasn't reached Firestore yet.
// 3. Fire createGroupJoinRequest and surface its outcome verbatim.
// 4. Any thrown error from the helper is swallowed and reported as
//    "error" — a bad URL must not block app entry; the user lands on
//    /(tabs)/groups either way.

import type { User } from "firebase/auth";
import type { Firestore } from "firebase/firestore";

import {
  createGroupJoinRequest,
  type CreateGroupJoinRequestResult,
} from "./joinGroupAfterSignup.web";
import {
  clearSignupParam,
  getPersistedSignupParams,
} from "./signupParams.web";

export type ProcessGroupIdParamOutcome =
  | "no-action"
  | CreateGroupJoinRequestResult
  | "error";

export interface ProcessGroupIdParamArgs {
  firestore: Firestore;
  user: User;
}

export const processGroupIdParam = async ({
  firestore,
  user,
}: ProcessGroupIdParamArgs): Promise<ProcessGroupIdParamOutcome> => {
  const groupId = getPersistedSignupParams()?.groupId;
  if (!groupId) return "no-action";

  clearSignupParam("groupId");

  try {
    return await createGroupJoinRequest({ firestore, user, groupId });
  } catch {
    return "error";
  }
};
